import React, { useState, useEffect } from 'react';
import { LayoutDashboard, AlertTriangle, TrendingUp, TrendingDown, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import apiService from '../../utils/api';
import { useToast } from '../../components/Toast';

function OwnerDashboardPage() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Overlay state: { kind: 'receivable'|'payable', overdueOnly: bool, title: string }
  const [overlay, setOverlay] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await apiService.getOwnerDashboard();
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load dashboard: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const fmt = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '-';

  const dueColor = (overdue, daysToDue) => {
    if (overdue) return 'var(--error-color)';
    if (daysToDue != null && daysToDue <= 3) return 'var(--warning-color)';
    return 'var(--text-secondary)';
  };

  const dueLabel = (overdue, daysToDue) => {
    if (overdue) return `Overdue by ${Math.abs(daysToDue)}d`;
    if (daysToDue == null) return '-';
    if (daysToDue === 0) return 'Due today';
    return `Due in ${daysToDue}d`;
  };

  const openOverlay = (kind, overdueOnly, title) => setOverlay({ kind, overdueOnly, title });
  const closeOverlay = () => setOverlay(null);

  const goToReceiptPage = () => navigate('/receipt');
  const goToPaymentPage = () => navigate('/payment-history');
  const goToLedger = (partyType) => navigate('/party-ledger');

  const renderOverlay = () => {
    if (!overlay || !data) return null;
    const rows = (overlay.kind === 'receivable' ? data.receivables : data.payables)
      .filter(r => !overlay.overdueOnly || r.overdue);

    return (
      <div
        onClick={closeOverlay}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem'
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="card"
          style={{ width: '100%', maxWidth: '900px', maxHeight: '85vh', overflow: 'auto', padding: '1.5rem' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0 }}>{overlay.title}</h2>
            <button className="btn-icon" onClick={closeOverlay} title="Close"><X size={20} /></button>
          </div>

          {rows.length === 0 ? (
            <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem' }}>Nothing here.</div>
          ) : overlay.kind === 'receivable' ? (
            <table className="data-table" style={{ fontSize: '0.9rem' }}>
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Customer</th>
                  <th>Invoice Date</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'right' }}>Paid</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, color: 'var(--primary-color)' }}>{r.invoice_number}</td>
                    <td>{r.customer_name}</td>
                    <td>{fmtDate(r.invoice_date)}</td>
                    <td>
                      {fmtDate(r.due_date)}
                      <div style={{ fontSize: '0.78rem', color: dueColor(r.overdue, r.days_to_due), fontWeight: 600 }}>
                        {dueLabel(r.overdue, r.days_to_due)}
                      </div>
                    </td>
                    <td>{r.overdue ? <span style={{ color: 'var(--error-color)', fontWeight: 600 }}>OVERDUE</span> : 'Pending'}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(r.total_amount)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{fmt(r.paid)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--success-color)' }}>{fmt(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="data-table" style={{ fontSize: '0.9rem' }}>
              <thead>
                <tr>
                  <th>Ref / Bill</th>
                  <th>Vendor</th>
                  <th>Kind</th>
                  <th>Bill Date</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'right' }}>Paid</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, color: 'var(--primary-color)' }}>{p.bill_no || p.ref}</td>
                    <td>{p.vendor_name}</td>
                    <td><span style={{ fontSize: '0.78rem', padding: '2px 8px', borderRadius: 12, background: 'var(--bg-secondary)' }}>{p.kind}</span></td>
                    <td>{fmtDate(p.date)}</td>
                    <td>
                      {fmtDate(p.due_date)}
                      <div style={{ fontSize: '0.78rem', color: dueColor(p.overdue, p.days_to_due), fontWeight: 600 }}>
                        {dueLabel(p.overdue, p.days_to_due)}
                      </div>
                    </td>
                    <td>{p.overdue ? <span style={{ color: 'var(--error-color)', fontWeight: 600 }}>OVERDUE</span> : 'Pending'}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(p.total_amount)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{fmt(p.paid)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--error-color)' }}>{fmt(p.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={closeOverlay}>Close</button>
            {overlay.kind === 'receivable' && (
              <button className="btn btn-primary" onClick={goToReceiptPage}>Go to Receipts</button>
            )}
            {overlay.kind === 'payable' && (
              <button className="btn btn-primary" onClick={goToPaymentPage}>Go to Payments</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const clickableCard = (style) => ({
    padding: '1.2rem', cursor: 'pointer', transition: 'transform 0.1s',
    ...style,
  });

  return (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">
          <div className="page-header">
            <h1 className="page-title"><LayoutDashboard size={24} style={{ marginRight: '0.5rem' }} /> Owner Dashboard</h1>
            <button className="btn btn-secondary" onClick={load} disabled={loading}>Refresh</button>
          </div>

          {error && <div className="error-container"><p>{error}</p></div>}
          {loading && !data && <div className="loading-container"><div className="spinner"></div><p>Loading…</p></div>}

          {data && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <div
                  className="card"
                  style={clickableCard({ borderLeft: '4px solid var(--success-color)' })}
                  onClick={() => openOverlay('receivable', false, 'All Receivables')}
                  title="Click to view all receivables"
                >
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}><TrendingUp size={14} /> Total Receivable</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--success-color)' }}>{fmt(data.summary.total_receivable)}</div>
                </div>
                <div
                  className="card"
                  style={clickableCard({ borderLeft: '4px solid var(--error-color)' })}
                  onClick={() => openOverlay('payable', false, 'All Payables')}
                  title="Click to view all payables"
                >
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}><TrendingDown size={14} /> Total Payable</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--error-color)' }}>{fmt(data.summary.total_payable)}</div>
                </div>
                <div
                  className="card"
                  style={clickableCard({ borderLeft: '4px solid var(--warning-color)' })}
                  onClick={() => openOverlay('receivable', true, 'Overdue Receivables')}
                  title="Click to view overdue receivables"
                >
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}><AlertTriangle size={14} /> Overdue Receivable</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--warning-color)' }}>
                    {fmt(data.summary.overdue_receivable_amount)} <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>({data.summary.overdue_receivable_count})</span>
                  </div>
                </div>
                <div
                  className="card"
                  style={clickableCard({ borderLeft: '4px solid var(--warning-color)' })}
                  onClick={() => openOverlay('payable', true, 'Overdue Payables')}
                  title="Click to view overdue payables"
                >
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}><AlertTriangle size={14} /> Overdue Payable</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--warning-color)' }}>
                    {fmt(data.summary.overdue_payable_amount)} <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>({data.summary.overdue_payable_count})</span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '1.5rem' }}>
                <div className="card" style={{ padding: '1.2rem' }}>
                  <h3 style={{ marginBottom: '0.8rem', color: 'var(--success-color)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <TrendingUp size={18} /> Payments to Receive
                  </h3>
                  {data.receivables.length === 0 ? (
                    <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem' }}>No pending receivables.</div>
                  ) : (
                    <div className="table-container" style={{ margin: 0, border: 'none' }}>
                      <table className="data-table" style={{ fontSize: '0.88rem' }}>
                        <thead>
                          <tr>
                            <th>Invoice</th>
                            <th>Customer</th>
                            <th>Due</th>
                            <th style={{ textAlign: 'right' }}>Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.receivables.map((r, i) => (
                            <tr key={i}>
                              <td style={{ fontWeight: 600, color: 'var(--primary-color)' }}>{r.invoice_number}</td>
                              <td>{r.customer_name}</td>
                              <td>
                                {fmtDate(r.due_date)}
                                <div style={{ fontSize: '0.78rem', color: dueColor(r.overdue, r.days_to_due), fontWeight: 600 }}>
                                  {dueLabel(r.overdue, r.days_to_due)}
                                </div>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--success-color)' }}>{fmt(r.balance)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="card" style={{ padding: '1.2rem' }}>
                  <h3 style={{ marginBottom: '0.8rem', color: 'var(--error-color)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <TrendingDown size={18} /> Payments to Make
                  </h3>
                  {data.payables.length === 0 ? (
                    <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem' }}>No pending payables.</div>
                  ) : (
                    <div className="table-container" style={{ margin: 0, border: 'none' }}>
                      <table className="data-table" style={{ fontSize: '0.88rem' }}>
                        <thead>
                          <tr>
                            <th>Ref / Bill</th>
                            <th>Vendor</th>
                            <th>Kind</th>
                            <th>Due</th>
                            <th style={{ textAlign: 'right' }}>Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.payables.map((p, i) => (
                            <tr key={i}>
                              <td style={{ fontWeight: 600, color: 'var(--primary-color)' }}>{p.bill_no || p.ref}</td>
                              <td>{p.vendor_name}</td>
                              <td><span style={{ fontSize: '0.78rem', padding: '2px 8px', borderRadius: 12, background: 'var(--bg-secondary)' }}>{p.kind}</span></td>
                              <td>
                                {fmtDate(p.due_date)}
                                <div style={{ fontSize: '0.78rem', color: dueColor(p.overdue, p.days_to_due), fontWeight: 600 }}>
                                  {dueLabel(p.overdue, p.days_to_due)}
                                </div>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--error-color)' }}>{fmt(p.balance)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          {renderOverlay()}
        </div>
      </div>
    </div>
  );
}

export default OwnerDashboardPage;
